/** @jsxImportSource @emotion/react */
import { skipToken } from '@reduxjs/toolkit/query/react'
import moment from 'moment'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { headingsPlugin, linkPlugin, listsPlugin, MDXEditor, quotePlugin, } from '@mdxeditor/editor'

import { useGetLabPayerQuery } from 'store/services/payer'

import '@mdxeditor/editor/style.css'

interface Props {
  open: boolean
  onClose: () => void
  labPayerId: number | null
  payerName?: string
}

const PayorNotesModal = ({ open, onClose, labPayerId, payerName }: Props) => {
  const { data: payer, isLoading } = useGetLabPayerQuery(
    open && labPayerId ? { id: String(labPayerId) } : skipToken,
  )

  const notes = [...(payer?.payerNotes ?? [])]
    .sort(
      (a, b) =>
        new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime(),
    )

  return (
    <Modal centered show={open} onHide={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>
          Verification Notes{payerName ? ` — ${payerName}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body css={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="text-muted p-2">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-muted p-2">No notes configured for this payor.</div>
        ) : (
          notes.map((note) => (
            <div
              key={note.uuid}
              className="border rounded p-3 mb-2"
              css={{ background: '#f8fbfd' }}>
              <div className="mb-1">
                <small className="text-muted">
                  {moment(note.createdAt as string).format('MM/DD/YYYY hh:mm A')}
                  {note.updatedAt && note.updatedAt !== note.createdAt && (
                    <span className="ml-2">
                      (edited {moment(note.updatedAt as string).format('MM/DD/YYYY hh:mm A')})
                    </span>
                  )}
                </small>
              </div>
              <div
                css={{
                  '& .mdxeditor': {
                    border: 'none',
                    padding: 0,
                    '& ._toolbarRoot_uazmk_1': { display: 'none' },
                  },
                }}>
                <MDXEditor
                  key={note.uuid}
                  readOnly
                  contentEditableClassName="markdown-editor"
                  markdown={note.note ?? ''}
                  plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    linkPlugin(),
                  ]}
                />
              </div>
            </div>
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PayorNotesModal
